/**
 * Test A/B de la barre de filtres mobile des listes (MarketplaceBarLayout, piloté par Nova → Tests A/B).
 *
 * La variante est rendue côté serveur (classe `mkt-bar--e2|e3` du body) ; pour un visiteur compté
 * dans le test, `window.__AB__` porte « control » (e2) ou « test » (e3). Ici, seulement l'exposition :
 * la première fois que la barre mobile entre dans l'écran, un événement `ab_exposure` part dans le
 * dataLayer, et PostHog reçoit `experiment_exposed` avec la variante, aussi posée sur la personne
 * (voir `analytics/posthog-exposure.ts`).
 */

import { sendExposure } from "../analytics/posthog-exposure";

export const MARKETPLACE_BAR_EXPERIMENT = "exp-202610-marketplace-bar";

const MOBILE_QUERY = "(max-width: 959.98px)";

let exposureSent = false;

/** « control » | « test » pour ce visiteur, null hors test (mode forcé, robot, repli). */
export function marketplaceBarAbValue(): "control" | "test" | null {
    if (typeof window === "undefined") return null;
    const value = window.__AB__?.[MARKETPLACE_BAR_EXPERIMENT];

    return value === "control" || value === "test" ? value : null;
}

function pushExposure(value: "control" | "test"): void {
    if (exposureSent) return;
    exposureSent = true;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: "ab_exposure",
        experiment: MARKETPLACE_BAR_EXPERIMENT,
        variant: value,
        ab_marketplace_bar: value,
    });
    sendExposure({ experiment: MARKETPLACE_BAR_EXPERIMENT, property: "ab_marketplace_bar", variant: value });
}

/** Barre de filtres de la liste (marketplace, ou bloc annonces d'une landing / page dynamique). */
function listingBar(): Element | null {
    return (
        document.querySelector(".marketplace-global-chrome__action-bar, .marketplace-excerpt__action-bar") ??
        document.querySelector(".marketplace-action-bar")
    );
}

/**
 * Une fois par page, quand la barre mobile entre réellement dans l'écran (au moins la moitié visible) :
 * sur une landing, elle peut être 2 000 px plus bas ; sur ordinateur (barre mobile absente), jamais.
 */
export function trackMarketplaceBarExposure(): void {
    const value = marketplaceBarAbValue();
    if (value === null || exposureSent || typeof window.matchMedia !== "function") return;
    if (typeof IntersectionObserver === "undefined") return;
    const bar = listingBar();
    if (!bar) return;

    const mobile = window.matchMedia(MOBILE_QUERY);
    let barOnScreen = false;
    let observer: IntersectionObserver | null = null;

    const stop = (): void => {
        observer?.disconnect();
        observer = null;
        mobile.removeEventListener("change", onMediaChange);
    };
    const exposeIfShown = (): void => {
        if (!barOnScreen || !mobile.matches) return;
        stop();
        pushExposure(value);
    };
    // Fenêtre rétrécie (ou tablette tournée) alors que la barre est déjà à l'écran
    function onMediaChange(): void {
        exposeIfShown();
    }

    observer = new IntersectionObserver(
        (entries) => {
            barOnScreen = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5);
            exposeIfShown();
        },
        { threshold: 0.5 },
    );
    observer.observe(bar);
    mobile.addEventListener("change", onMediaChange);
}

/** Tests uniquement. */
export function resetMarketplaceBarExposureForTests(): void {
    exposureSent = false;
}
